import { useEffect, useRef } from "react";
import {
  eventPlace,
  formatTimeRange,
  layoutDay,
  onDay,
  onlineLabel,
  startOfDay,
  uniqueById,
  wallMinutes,
} from "../../helpers/calendar";
import { colors } from "../../style";
import { Icon } from "../ui";
import { eventColor, eventTitle, isDeclined } from "./common";

export const HOUR_PX = 48;
const GUTTER = 52;
const MIN_EVENT_PX = 22;
const hatched = `repeating-linear-gradient(135deg, transparent 0 6px, ${colors.border} 6px 7px)`;

// Hour grid for one or more days. days: moments; syncedUntil: days after it
// aren't in the API's window, so they're hatched rather than shown as free.
function CalendarTimeline({ days, events, now, syncedUntil, onOpen }) {
  const scroller = useRef();
  const showToday = days.some((d) => d.isSame(now, "day"));
  const allDay = days.map((d) =>
    uniqueById(events.filter((e) => e.is_all_day && onDay(e, d))),
  );
  const hasAllDay = allDay.some((x) => x.length > 0);
  const timedByDay = days.map((d) =>
    events.filter((e) => !e.is_all_day && onDay(e, d)),
  );
  const columns = `${GUTTER}px repeat(${days.length}, minmax(0, 1fr))`;
  const key = days.map((d) => d.format("YYYYMMDD")).join();

  // Open on the hour before now if today is shown, else the first event
  useEffect(() => {
    const firstStart = timedByDay
      .flat()
      .map((e) => e.start.hours() + e.start.minutes() / 60)
      .sort((a, b) => a - b)[0];
    const hour = showToday ? now.hours() - 1 : Math.floor(firstStart ?? 8) - 1;
    if (scroller.current) {
      scroller.current.scrollTop = Math.max(0, hour) * HOUR_PX;
    }
    // Only when the visible days change, not every minute
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <div
      ref={scroller}
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        borderTop: `1px solid ${colors.border}`,
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 3,
          background: colors.pageBackground,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        {days.length > 1 && (
          <div style={{ display: "grid", gridTemplateColumns: columns }}>
            <div />
            {days.map((d) => (
              <DayHeading
                key={d.format()}
                day={d}
                now={now}
                unsynced={d.isAfter(syncedUntil, "day")}
              />
            ))}
          </div>
        )}
        {hasAllDay && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: columns,
              alignItems: "start",
              padding: "4px 0 6px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                color: colors.textFaint,
                paddingTop: "4px",
              }}
            >
              All day
            </div>
            {allDay.map((list, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "3px",
                  padding: "0 3px",
                  minWidth: 0,
                }}
              >
                {list.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    className="sk-cal-event"
                    title={eventTitle(e)}
                    onClick={() => onOpen(e)}
                    style={{
                      ...eventBox(e),
                      position: "static",
                      padding: "3px 8px",
                      fontSize: "12px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                    }}
                  >
                    {eventTitle(e)}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: columns,
          height: 24 * HOUR_PX,
          paddingBottom: "12px",
        }}
      >
        {Array.from({ length: 24 }, (_, h) => (
          <div
            key={h}
            aria-hidden="true"
            style={{
              position: "absolute",
              top: h * HOUR_PX,
              left: GUTTER,
              right: 0,
              borderTop: h ? `1px solid ${colors.border}` : 0,
            }}
          >
            {h > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-7px",
                  left: -GUTTER,
                  width: GUTTER - 8,
                  textAlign: "right",
                  fontSize: "11px",
                  color: colors.textFaint,
                }}
              >
                {String(h).padStart(2, "0")}:00
              </span>
            )}
          </div>
        ))}

        <div />
        {days.map((d, i) => {
          const isToday = d.isSame(now, "day");
          const unsynced = d.isAfter(syncedUntil, "day");
          return (
            <div
              key={d.format()}
              title={unsynced ? "Not synced yet" : undefined}
              style={{
                position: "relative",
                borderLeft: `1px solid ${colors.border}`,
                background: unsynced
                  ? hatched
                  : isToday && days.length > 1
                    ? "rgba(88, 185, 183, 0.04)"
                    : undefined,
              }}
            >
              {layoutDay(timedByDay[i], d, {
                minMinutes: (MIN_EVENT_PX / HOUR_PX) * 60,
              }).map((p) => (
                <EventBlock
                  key={`${p.event.id}-${p.event.start.valueOf()}`}
                  placed={p}
                  // A third of a week column has room for the title only
                  narrow={days.length > 1 && p.columns >= 3}
                  now={now}
                  onOpen={onOpen}
                />
              ))}
              {isToday && <NowLine now={now} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayHeading({ day, now, unsynced }) {
  const isToday = day.isSame(now, "day");
  const past = day.isBefore(startOfDay(now));
  return (
    <div
      aria-current={isToday ? "date" : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        padding: "8px 4px",
        opacity: past || unsynced ? 0.55 : 1,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: isToday ? colors.teal : colors.textLight,
        }}
      >
        {day.format("ddd")}
      </span>
      <span
        style={{
          minWidth: "26px",
          height: "26px",
          borderRadius: "999px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "15px",
          fontWeight: 600,
          background: isToday ? colors.teal : "none",
          color: isToday ? colors.onPrimary : colors.text,
        }}
      >
        {day.date()}
      </span>
    </div>
  );
}

const eventBox = (e) => {
  const c = eventColor(e);
  const declined = isDeclined(e);
  return {
    boxSizing: "border-box",
    borderRadius: "7px",
    border: declined ? `1px dashed ${c}80` : 0,
    borderLeft: `3px solid ${c}`,
    background: declined ? "transparent" : `${c}2e`,
    color: colors.text,
    textAlign: "left",
    fontFamily: "inherit",
    cursor: "pointer",
    textDecoration: declined ? "line-through" : undefined,
  };
};

function EventBlock({ placed, narrow, now, onOpen }) {
  const { event, start, end, column, columns } = placed;
  const height = Math.max(MIN_EVENT_PX, ((end - start) / 60) * HOUR_PX - 2);
  const compact = height < 40;
  const past = event.end.isBefore(now);
  const online = onlineLabel(event);
  const meta = [formatTimeRange(event), online, eventPlace(event)]
    .filter(Boolean)
    .join(" · ");

  return (
    <button
      type="button"
      className="sk-cal-event"
      title={`${eventTitle(event)}\n${meta}`}
      onClick={() => onOpen(event)}
      style={{
        ...eventBox(event),
        position: "absolute",
        top: (start / 60) * HOUR_PX + 1,
        height,
        left: `calc(${(column / columns) * 100}% + 2px)`,
        width: `calc(${100 / columns}% - 4px)`,
        padding: narrow ? "3px 4px" : compact ? "2px 6px" : "4px 7px",
        display: "flex",
        flexDirection: compact ? "row" : "column",
        alignItems: compact ? "center" : "stretch",
        gap: compact ? "6px" : "1px",
        overflow: "hidden",
        opacity: past || isDeclined(event) ? 0.55 : 1,
        zIndex: 1,
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          minWidth: 0,
          flexShrink: compact ? 1 : 0,
          fontSize: "12.5px",
          fontWeight: 600,
          lineHeight: 1.3,
        }}
      >
        {event.is_online && !narrow && (
          <Icon name="video" size={12} color={eventColor(event)} />
        )}
        <span
          style={{
            overflow: "hidden",
            textOverflow: narrow ? "clip" : "ellipsis",
            whiteSpace: compact ? "nowrap" : "normal",
            // Wrap long titles in tall blocks, up to two lines
            display: compact ? "block" : "-webkit-box",
            WebkitLineClamp: height >= 64 ? 2 : 1,
            WebkitBoxOrient: "vertical",
          }}
        >
          {eventTitle(event)}
        </span>
      </span>
      {!narrow && (
        <span
          style={{
            fontSize: "11.5px",
            lineHeight: 1.3,
            color: colors.textMuted,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
            // In one-line blocks the title keeps its room first
            flexShrink: 100,
          }}
        >
          {meta}
        </span>
      )}
    </button>
  );
}

function NowLine({ now }) {
  const minutes = Math.floor(wallMinutes(now));
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        top: (minutes / 60) * HOUR_PX,
        left: 0,
        right: 0,
        height: "2px",
        background: colors.red,
        zIndex: 2,
        pointerEvents: "none",
      }}
    >
      <span
        style={{
          position: "absolute",
          left: "-5px",
          top: "-4px",
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          background: colors.red,
        }}
      />
    </div>
  );
}

export default CalendarTimeline;
